// ─────────────────────────────────────────────────────────────────────────────
// PWA runtime: install-prompt plumbing and display-mode detection. (The
// service-worker registration is src/lib/registerSw.js.)
//
// The install story is asymmetric by platform and this module absorbs that:
//
//   Chromium          Edge / Chrome on Windows (the reception desk — the
//   (Windows,         case this panel is installed for) and on Android fire
//    Android)         `beforeinstallprompt`. The event MUST be captured
//                     (and preventDefault'd) before any UI exists — it often
//                     fires while React is still mounting — so App.jsx calls
//                     initPwa() at module scope and the captured event waits
//                     here until the banner asks for it.
//   iOS Safari        has no install event at all. Installability is inferred
//                     (iOS device, not already standalone) and the banner
//                     shows share-sheet instructions instead of a prompt.
//
// Everything is defensive about environment: under jsdom there is no
// matchMedia and no service worker; in dev/e2e the worker must stay out of
// the way (a worker caching a dev server turns every reload into archaeology).
// ─────────────────────────────────────────────────────────────────────────────

const DISMISS_KEY = 'parsina.pwa.dismissedAt';
const INSTALLED_KEY = 'parsina.pwa.installed';

// A dismissed banner stays gone for two weeks — long enough to not nag,
// short enough that "I'll do it later" gets a second chance.
export const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;

let deferredPrompt = null;
let initialized = false;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => { try { fn(); } catch { /* listener's problem */ } });

const storageGet = (key) => {
  try { return window.localStorage.getItem(key); } catch { return null; }
};
const storageSet = (key, value) => {
  try { window.localStorage.setItem(key, value); } catch { /* private mode */ }
};

// ── Environment detection ────────────────────────────────────────────────────

/** Running as an installed app (home-screen launch), on any platform. */
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  // A Windows install can run as `window-controls-overlay` (the user toggled
  // the title-bar chevron) — still the installed app, still no banner.
  if (typeof window.matchMedia === 'function'
      && ['standalone', 'window-controls-overlay', 'minimal-ui']
        .some((mode) => window.matchMedia(`(display-mode: ${mode})`).matches)) return true;
  // Legacy iOS signal, still the only one some Safari versions expose.
  return window.navigator.standalone === true;
}

/** iPhone, iPod, or iPad — including iPadOS 13+, which masquerades as a Mac. */
export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
}

// ── Install prompt state ─────────────────────────────────────────────────────

function isDismissed() {
  const raw = storageGet(DISMISS_KEY);
  if (!raw) return false;
  const at = Number(raw);
  return Number.isFinite(at) && Date.now() - at < DISMISS_COOLDOWN_MS;
}

/**
 * One snapshot for the banner to render from. `visible` folds in every gate:
 * something to offer (a captured prompt, or iOS where instructions are the
 * offer), not already installed or standalone, and not recently dismissed.
 */
export function getInstallState() {
  const standalone = isStandalone();
  const ios = isIOS();
  const installed = storageGet(INSTALLED_KEY) === 'true';
  const dismissed = isDismissed();
  return {
    standalone,
    ios,
    promptAvailable: deferredPrompt !== null,
    visible: !standalone && !installed && !dismissed && (deferredPrompt !== null || ios),
  };
}

/** Subscribe to install-state changes. Returns the unsubscribe function. */
export function subscribeInstallPrompt(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Show the captured native prompt (Android/Chromium). Resolves to the user's
 * choice — 'accepted' | 'dismissed' — or null when no prompt was available.
 * The prompt is single-use; either outcome consumes it.
 */
export async function promptInstall() {
  const prompt = deferredPrompt;
  if (!prompt) return null;
  deferredPrompt = null;
  try {
    prompt.prompt();
    const choice = await prompt.userChoice;
    const outcome = choice && choice.outcome === 'accepted' ? 'accepted' : 'dismissed';
    if (outcome === 'dismissed') storageSet(DISMISS_KEY, String(Date.now()));
    return outcome;
  } catch {
    return 'dismissed';
  } finally {
    notify();
  }
}

/** «بعداً» — start the cooldown and hide the banner everywhere. */
export function dismissInstallPrompt() {
  storageSet(DISMISS_KEY, String(Date.now()));
  notify();
}

// ── Init ─────────────────────────────────────────────────────────────────────
// (Service-worker registration lives in src/lib/registerSw.js. App.jsx calls
// both.)

/**
 * Attach the platform listeners. Called once from App.jsx at module scope —
 * before the first render — so an early `beforeinstallprompt` is never
 * missed. Idempotent.
 */
export function initPwa() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    // Chrome's mini-infobar is suppressed in favour of the in-app banner.
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    storageSet(INSTALLED_KEY, 'true');
    notify();
  });

  initLaunchQueue();
}

// ── Launches into the running window (Windows) ───────────────────────────────
//
// The manifest asks for `launch_handler: focus-existing`: a Start-menu or
// jump-list launch while the panel is already open FOCUSES that window
// instead of stacking a second one on the reception PC (two windows = two
// sessionStorage sessions and two sign-ins). The price is that the browser
// does not navigate the focused window itself — it hands the target URL to
// `window.launchQueue`, and this consumer routes it in-app.

/**
 * Where a launch should land, given the path the window is on now.
 *
 * Shortcut URLs are clinic-less (`/reception`) because the manifest cannot
 * know the clinic. Inside `/c/<ref>/…` the target is re-rooted under that
 * same clinic — a jump-list click at the Isfahan branch must not bounce to
 * the person's default clinic, which is what `LegacyPathRedirect` would do.
 * Returns null when there is nothing to do (bare start_url launch, foreign
 * origin, or already there).
 */
export function launchPathFor(targetUrl, currentPath, origin) {
  let url;
  try { url = new URL(targetUrl, origin); } catch { return null; }
  if (url.origin !== origin) return null;
  if (url.pathname === '/' && !url.search) return null; // plain launch: just focus
  let path = url.pathname;
  const inClinic = /^\/c\/[^/]+/.exec(currentPath || '');
  if (inClinic && !path.startsWith('/c/')) path = inClinic[0] + path;
  const next = path + url.search;
  return next === currentPath ? null : next;
}

function initLaunchQueue() {
  if (!('launchQueue' in window) || typeof window.launchQueue?.setConsumer !== 'function') return;
  window.launchQueue.setConsumer((params) => {
    if (!params || !params.targetURL) return;
    const current = window.location.pathname + window.location.search;
    const next = launchPathFor(params.targetURL, current, window.location.origin);
    if (!next) return;
    // BrowserRouter listens to popstate, so this is an in-app navigation:
    // no reload, no lost state in other screens' caches, no second SSO bounce.
    window.history.pushState({}, '', next);
    window.dispatchEvent(new PopStateEvent('popstate'));
  });
}

/** Test seam: reset module state between cases. Not used by app code. */
export function __resetPwaState() {
  deferredPrompt = null;
  initialized = false;
  listeners.clear();
}
